import { Loading } from '@/components/Loading';
import { useAccessoryStore } from '@/stores/useAccessoryStore';
import i18n from '@/translations/config';
import { getUrlForLink } from '@/utils/getUrlForLink';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

export const ScanAccessory = () => {
  const { uuid } = useParams();

  const { isLoading, accessory, error, scanAccessory } = useAccessoryStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = i18n.language;
  const hasRedirected = useRef(false);
  // Effet pour charger l'accessoire
  useEffect(() => {
    const loadAccessory = async () => {
      if (!uuid) return;

      try {
        await scanAccessory(uuid);
      } catch (err) {
        navigate(`/${lang}/error`, {
          replace: true,
          state: {
            status: 404,
            statusText: error || t('accessory:not-found'),
          },
        });
      }
    };

    loadAccessory();
  }, [uuid, scanAccessory, navigate, t, error, lang]);

  // Effet pour gérer les redirections une fois l'accessoire chargé
  useEffect(() => {
    if (!isLoading && accessory) {
      if (accessory.status === 'linked' && accessory.card) {
        if (accessory.directLink && accessory.directLink.url) {
          const formattedUrl = getUrlForLink(
            accessory.directLink.icon,
            accessory.directLink.url,
          );
          if (formattedUrl && !hasRedirected.current) {
            hasRedirected.current = true;
            if (
              formattedUrl.startsWith('mailto:') ||
              formattedUrl.startsWith('tel:') ||
              formattedUrl.startsWith('sms:')
            ) {
              window.open(formattedUrl, '_self');
            } else {
              window.location.href = formattedUrl;
            }
          }
          return;
        }
        navigate(`/${lang}/profile/${accessory.card.slug}`, {
          replace: true,
        });
        return;
      }

      // Cas: Accessoire désactivé
      if (accessory.status === 'disabled') {
        navigate(`/${lang}/error`, {
          replace: true,
          state: {
            status: 403,
            statusText: t('accessory:disabled-message'),
          },
        });
        return;
      }
    }
  }, [accessory, isLoading, navigate, t, lang]);

  // Affichage pendant le chargement
  if (isLoading) {
    return <Loading />;
  }

  // Cas: Accessoire prêt à être lié (unlinked)
  if (accessory && accessory.status === 'unlinked' && uuid) {
    return (
      <Navigate to={`/${lang}/dashboard/accessory/${uuid}/link`} replace />
    );
  }

  // Fallback - ne devrait pas arriver car tous les cas sont gérés dans les useEffect
  return null;
};
